import { useEffect, useState, useCallback } from "react";
import { Upload, Download, CheckCircle2, XCircle, Loader2, FileText, ShieldCheck } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TooltipButton } from "@/components/common/Tooltips";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Etiquetas visibles para tipos que se almacenan con clave snake_case en BD
// (usadas por Cartera y por la Solicitud) para que se rendericen "bonitos" en el expediente.
const TIPO_LABEL = {
  contrato_firmado: "Contrato firmado",
};
const labelDeTipo = (t) => TIPO_LABEL[t] || t;
// Tipos "extra" que se pueden subir desde el diálogo del expediente además del checklist KYC.
// Cada item puede ser string (label = value) o {label, value} cuando el value en BD no es user-friendly.
const EXTRA_TIPOS = [
  "Comprobante de domicilio", "Contrato", "Pagaré", "Autorización de buró",
  "Aviso de privacidad", "Acuse de reporte", "Carta de autorización (override)", "Otro",
  { label: "Declaración de actuación por cuenta propia", value: "Declaración de actuación por cuenta propia" },
  { label: "Contrato firmado", value: "contrato_firmado" },
];

export function ExpedienteDigital({ clienteId, cliente, categoria = "expediente", onChange }) {
  const { user, can } = useAuth();
  const esValidador = ["super_admin", "oficial_cumplimiento", "admin_entidad"].includes(user?.role);
  const puedeSubir = can("documentos", "crear");
  const [checklist, setChecklist] = useState(null);
  const [datosFiscales, setDatosFiscales] = useState(null);
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    // Contexto override (Fase 43ª): NO carga el checklist KYC (no aplica a la carta de
    // autorización) y filtra la tabla por categoría para mostrar SOLO las cartas cargadas.
    // Contexto por defecto ("expediente"): comportamiento previo intacto.
    const esOverride = categoria === "override";
    const docsParams = esOverride
      ? { cliente_id: clienteId, categoria }
      : { cliente_id: clienteId };
    const [cl, dl, cli] = await Promise.all([
      esOverride
        ? Promise.resolve(null)
        : api.get(`/documentos/checklist/${clienteId}`).then((r) => r.data).catch(() => null),
      api.get(`/documentos`, { params: docsParams }).then((r) => r.data),
      // feb-2026 · sub-panel Datos fiscales — Trae RFC / régimen / CP fiscal / uso CFDI
      // del cliente para pintarlos junto al checklist documental. Silencioso si falla.
      esOverride
        ? Promise.resolve(null)
        : api.get(`/clientes/${clienteId}`).then((r) => r.data).catch(() => null),
    ]);
    setChecklist(cl);
    setDatosFiscales(cli);
    setDocs(dl.filter((d) => d.archivo_key));
  }, [clienteId, categoria]);

  useEffect(() => { if (clienteId) load(); }, [clienteId, load]);

  const subir = async () => {
    if (!tipo || !fileObj) { toast.error("Selecciona tipo y archivo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("tipo_documento", tipo);
      fd.append("categoria", categoria);
      fd.append("cliente_id", clienteId);
      fd.append("clasificacion", "Soporte");
      await api.post("/documentos/upload", fd);
      toast.success("Archivo cargado y cifrado");
      setOpen(false); setTipo(""); setFileObj(null);
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const descargar = async (doc) => {
    try {
      const { data } = await api.post(`/documentos/${doc.id}/signed-url`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const decidir = async (doc, accion) => {
    try {
      if (accion === "validar") await api.post(`/documentos/${doc.id}/validar`);
      else await api.post(`/documentos/${doc.id}/rechazar`, { motivo: "Documento ilegible o no corresponde" });
      toast.success(accion === "validar" ? "Documento validado" : "Documento rechazado");
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const tiposDisponibles = [
    ...(checklist?.items.map((i) => i.tipo_documento) || []),
    ...EXTRA_TIPOS,
  ];
  // Normaliza a {label, value} y deduplica por value.
  const tiposOpciones = (() => {
    const out = [];
    const vistos = new Set();
    for (const t of tiposDisponibles) {
      const value = typeof t === "string" ? t : t.value;
      const label = typeof t === "string" ? t : t.label;
      if (vistos.has(value)) continue;
      vistos.add(value);
      out.push({ value, label });
    }
    return out;
  })();

  // Sugerencia NO bloqueante de diligencia reforzada: cliente medio/alto sin comprobante de domicilio.
  const grado = cliente?.grado_riesgo || cliente?.nivel_riesgo;
  const tieneComprobante = docs.some((d) => d.tipo_documento === "Comprobante de domicilio");
  const sugerirComprobante = ["Medio", "Alto"].includes(grado) && !tieneComprobante;

  return (
    <div data-testid="expediente-digital">
      {sugerirComprobante && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="exp-comprobante-sugerencia">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Cliente de riesgo <b>{grado}</b>: se sugiere (no obligatorio) cargar un <b>comprobante de domicilio</b> como control de diligencia reforzada, además de la INE.</span>
        </div>
      )}
      <div className="mb-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        Archivos cifrados en reposo, con hash SHA-256 y descarga por enlace temporal autenticado. Conservación {checklist ? "10" : "10"} años; los documentos no se eliminan, solo cambian de estatus.
      </div>

      {checklist && (
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">
              Checklist KYC ({checklist.tipo_persona}) — {checklist.completos}/{checklist.total} vigentes
              <span className="ml-2"><StatusBadge value={checklist.expediente_estatus} /></span>
            </p>
            <Button data-testid="exp-subir-btn" size="sm" onClick={() => setOpen(true)} disabled={!puedeSubir} className="bg-primary hover:bg-sidebar-hover">
              <Upload className="mr-1.5 h-4 w-4" /> Subir documento
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {checklist.items.map((i) => (
              <div key={i.tipo_documento} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`checklist-item-${i.estatus}`}>
                <span className="flex items-center gap-2 text-slate-700">
                  <FileText className="h-4 w-4 text-slate-400" /> {i.tipo_documento}
                  {i.obligatorio === false && <span className="text-[10px] text-slate-400">(opcional)</span>}
                </span>
                <StatusBadge value={i.estatus} />
              </div>
            ))}
          </div>
          {/* feb-2026 · Sub-panel Datos fiscales — el checklist documental solo mira archivos;
              estos escalares del cliente (RFC / régimen / CP / uso CFDI) faltaban en la UI y
              por eso todo aparecía verde aunque no se pudiera emitir CFDI. */}
          {datosFiscales && (() => {
            const items = [
              { key: "rfc", label: "RFC", ok: !!String(datosFiscales?.rfc || "").trim() },
              // ago-2026 · iter125 — El campo real en `db.clientes` es `regimen_fiscal_sat`
              // (fuente única: server.py EXPEDIENTE_FIELDS). Leer `regimen_fiscal` pintaba
              // "Faltante" a los 93 clientes que SÍ lo tienen capturado. Se conserva el
              // fallback al nombre legacy para los 7 registros históricos.
              { key: "regimen_fiscal", label: "Régimen fiscal SAT",
                ok: !!String(datosFiscales?.regimen_fiscal_sat
                             || datosFiscales?.regimen_fiscal || "").trim() },
              { key: "codigo_postal_fiscal", label: "CP fiscal",
                ok: (() => { const v = String(datosFiscales?.codigo_postal_fiscal || "").trim();
                             return v.length === 5 && /^\d+$/.test(v); })() },
              { key: "uso_cfdi_default", label: "Uso de CFDI",
                ok: !!String(datosFiscales?.uso_cfdi_default || "").trim() },
            ];
            const completos = items.filter((it) => it.ok).length;
            return (
              <div className="mt-4">
                <p className="mb-2 text-sm font-medium text-slate-700">
                  Datos fiscales (CFDI 4.0) — {completos}/{items.length} completos
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {items.map((it) => (
                    <div
                      key={`fisc-${it.key}`}
                      data-testid={`checklist-fiscal-${it.key}`}
                      className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    >
                      <span className="flex items-center gap-2 text-slate-700">
                        <FileText className="h-4 w-4 text-slate-400" /> {it.label}
                      </span>
                      <StatusBadge value={it.ok ? "Completo" : "Faltante"} />
                    </div>
                  ))}
                </div>
                {completos < items.length && (
                  <p className="mt-2 text-[11px] text-slate-500">
                    Estos campos se capturan en el formulario del cliente. Sin ellos no se
                    puede emitir CFDI ni contratar el crédito.
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      <DataTable testid="table-expediente-docs" rows={docs}
        columns={[
          { key: "tipo_documento", label: "Documento", render: (r) => labelDeTipo(r.tipo_documento) },
          { key: "nombre_archivo", label: "Archivo" },
          { key: "estatus", label: "Estatus", kind: "badge" },
          { key: "hash_sha256", label: "Hash", render: (r) => <span className="font-mono text-xs text-slate-500">{String(r.hash_sha256 || "").slice(0, 10)}</span> },
          { key: "fecha_carga", label: "Carga", kind: "date" },
          {
            key: "acciones", label: "Acciones", render: (r) => (
              <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                <TooltipButton tooltip="Descargar el archivo (enlace firmado, válido 5 minutos)"
                  data-testid="exp-descargar-btn" size="sm" variant="outline" className="h-7 px-2" onClick={() => descargar(r)}>
                  <Download className="h-3.5 w-3.5" />
                </TooltipButton>
                {esValidador && r.estatus !== "Vigente" && (
                  <TooltipButton tooltip="Marcar el documento como válido (solo Cumplimiento/Administrador)"
                    size="sm" variant="outline" className="h-7 px-2 text-emerald-700" onClick={() => decidir(r, "validar")}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </TooltipButton>
                )}
                {esValidador && r.estatus !== "Rechazado" && (
                  <TooltipButton tooltip="Rechazar el documento (queda el motivo en bitácora)"
                    size="sm" variant="outline" className="h-7 px-2 text-red-600" onClick={() => decidir(r, "rechazar")}>
                    <XCircle className="h-3.5 w-3.5" />
                  </TooltipButton>
                )}
              </div>
            )
          },
        ]} emptyLabel="Sin archivos cargados. Usa 'Subir documento'." />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="exp-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Subir documento del expediente</DialogTitle>
            <DialogDescription className="sr-only">Carga de archivo cifrado al expediente del cliente</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs">Tipo de documento</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger data-testid="exp-tipo-select"><SelectValue placeholder="Selecciona tipo" /></SelectTrigger>
                <SelectContent>
                  {tiposOpciones.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Archivo (solo PDF — máx. 15 MB)</Label>
              <Input data-testid="exp-file-input" type="file" accept="application/pdf,.pdf"
                onChange={(e) => setFileObj(e.target.files?.[0] || null)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="exp-upload-guardar" onClick={subir} disabled={busy} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Cargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
